import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../util/routes';
import { createSlug } from '../../../util/urlHelpers';
import { obfuscatedCoordinates } from '../../../util/maps';

import SearchMapInfoCard from '../SearchMapInfoCard/SearchMapInfoCard';

import css from './SearchMapClustered.module.css';

const FIT_MAX_ZOOM = 12;
const WORLD_TILE_SIZE = 256;

/**
 * Lowest zoom whose mercator square fills the container. Below this, Google Maps
 * paints grey gutters around the world — obvious on tall mobile map viewports.
 *
 * @param {number} width
 * @param {number} height
 * @returns {number}
 */
export const minZoomToFillContainer = (width, height) => {
  const size = Math.max(width || 0, height || 0);
  if (size <= 0) {
    return 2;
  }
  return Math.max(2, Math.ceil(Math.log2(size / WORLD_TILE_SIZE)));
};

const getGoogleMaps = () =>
  typeof window !== 'undefined' && window.google ? window.google.maps : null;

// Resolve the coordinate to render the marker at, applying fuzzy obfuscation when enabled.
// Returns a plain { lat, lng } literal that the Google Maps API accepts directly.
const markerCoordinate = (listing, fuzzyConfig) => {
  const geolocation = listing?.attributes?.geolocation;
  if (!geolocation) {
    return null;
  }
  const coordinate = fuzzyConfig?.enabled
    ? obfuscatedCoordinates(
        geolocation,
        fuzzyConfig.offset,
        `${listing.id.uuid}_${geolocation.lat}_${geolocation.lng}`
      )
    : geolocation;
  return { lat: coordinate.lat, lng: coordinate.lng };
};

/**
 * Map of all pickup-enabled listings using Google Maps + @googlemaps/markerclusterer.
 *
 * This is the Google Maps counterpart to SearchMapClusteredMapbox. The provider-aware selector
 * (SearchMapClustered) picks the right implementation from config.maps.mapProvider. Clusters zoom
 * in on click; an individual marker opens the reused SearchMapInfoCard in an InfoWindow. The map
 * auto-fits to the dataset and does not trigger a new search when panned/zoomed.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {Array<propTypes.listing>} [props.listings] - Listing entities (with geolocation)
 * @param {Function} [props.onActivateListing] - Optional active-listing setter
 * @returns {JSX.Element}
 */
const SearchMapClusteredGoogleMaps = props => {
  const { className, rootClassName, listings = [], onActivateListing } = props;

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const clustererRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const listingsRef = useRef(listings);
  listingsRef.current = listings;

  const [isMapReady, setIsMapReady] = useState(false);
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [infoContainer, setInfoContainer] = useState(null);

  const fuzzyConfig = config.maps?.fuzzy;

  const createURLToListing = listing => {
    const id = listing.id.uuid;
    const slug = createSlug(listing.attributes.title);
    return createResourceLocatorString('ListingPage', routeConfiguration, { id, slug }, {});
  };

  const onListingInfoCardClicked = listing => {
    history.push(createURLToListing(listing));
  };

  // Initialize the map once the Google Maps library is available AND the container has a non-zero
  // size (Google Maps, like Mapbox, sizes its canvas at init time and would stay blank if the
  // container is 0x0). We poll until both conditions are met. A ResizeObserver keeps the canvas
  // matched to the flex/mobile panel — otherwise leftover grey gutters sit above the tiles until
  // the user pinches zoom.
  useEffect(() => {
    let cancelled = false;
    let pollId = null;
    let resizeObserver = null;
    let mapClickListener = null;
    let lastWidth = 0;
    let lastHeight = 0;

    const applyContainerSize = (googleMaps, map, container) => {
      const width = container.offsetWidth;
      const height = container.offsetHeight;
      if (width === lastWidth && height === lastHeight) {
        return;
      }
      lastWidth = width;
      lastHeight = height;
      const minZoom = minZoomToFillContainer(width, height);
      map.setOptions({ minZoom });
      googleMaps.event.trigger(map, 'resize');
    };

    const tryInit = () => {
      if (cancelled || mapRef.current) {
        return true;
      }
      const googleMaps = getGoogleMaps();
      const container = mapContainerRef.current;
      if (!googleMaps || !container) {
        return false;
      }
      if (container.offsetWidth === 0 || container.offsetHeight === 0) {
        return false;
      }

      const minZoom = minZoomToFillContainer(container.offsetWidth, container.offsetHeight);
      const map = new googleMaps.Map(container, {
        center: { lat: 20, lng: 0 },
        zoom: Math.max(2, minZoom),
        minZoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      });
      mapRef.current = map;

      // Marker clicks do not fire map `click` in the Maps JS API, so this only
      // dismisses the InfoWindow when tapping empty map (mobile tap-outside).
      mapClickListener = map.addListener('click', () => setSelectedListingId(null));

      const infoWindow = new googleMaps.InfoWindow({ headerDisabled: true });
      infoWindow.addListener('closeclick', () => setSelectedListingId(null));
      infoWindowRef.current = infoWindow;

      clustererRef.current = new MarkerClusterer({ map, markers: [] });

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          if (cancelled || !mapRef.current) {
            return;
          }
          applyContainerSize(googleMaps, mapRef.current, container);
        });
        resizeObserver.observe(container);
      }

      setIsMapReady(true);
      return true;
    };

    if (!tryInit()) {
      pollId = setInterval(() => {
        if (tryInit()) {
          clearInterval(pollId);
          pollId = null;
        }
      }, 150);
    }

    return () => {
      cancelled = true;
      if (pollId) {
        clearInterval(pollId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (mapClickListener) {
        const googleMaps = getGoogleMaps();
        if (googleMaps) {
          googleMaps.event.removeListener(mapClickListener);
        }
      }
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rebuild markers and auto-fit the viewport whenever the dataset changes.
  useEffect(() => {
    const googleMaps = getGoogleMaps();
    const map = mapRef.current;
    if (!googleMaps || !map || !isMapReady) {
      return;
    }

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }
    markersRef.current.forEach(marker => marker.setMap(null));

    const bounds = new googleMaps.LatLngBounds();
    const listingsWithLocation = listings.filter(l => !!l?.attributes?.geolocation);
    const markers = listingsWithLocation.reduce((acc, listing) => {
      const coordinate = markerCoordinate(listing, fuzzyConfig);
      if (!coordinate) {
        return acc;
      }
      const marker = new googleMaps.Marker({ position: coordinate });
      marker.addListener('click', () => {
        setSelectedListingId(listing.id.uuid);
        if (onActivateListing) {
          onActivateListing(listing.id.uuid);
        }
      });
      bounds.extend(coordinate);
      acc.push(marker);
      return acc;
    }, []);

    markersRef.current = markers;
    if (clustererRef.current) {
      clustererRef.current.addMarkers(markers);
    }

    if (markers.length > 0) {
      map.fitBounds(bounds);
      googleMaps.event.addListenerOnce(map, 'idle', () => {
        if (map.getZoom() > FIT_MAX_ZOOM) {
          map.setZoom(FIT_MAX_ZOOM);
        }
      });
    }
  }, [listings, isMapReady, fuzzyConfig, onActivateListing]);

  // Manage the info-card popup (InfoWindow) for the selected listing.
  useEffect(() => {
    const googleMaps = getGoogleMaps();
    const map = mapRef.current;
    const infoWindow = infoWindowRef.current;
    if (!googleMaps || !map || !infoWindow) {
      return undefined;
    }

    if (!selectedListingId) {
      infoWindow.close();
      setInfoContainer(null);
      return undefined;
    }

    const listing = listingsRef.current.find(l => l.id.uuid === selectedListingId);
    const coordinate = markerCoordinate(listing, fuzzyConfig);
    if (!coordinate) {
      return undefined;
    }

    const container = document.createElement('div');
    infoWindow.setContent(container);
    infoWindow.setPosition(coordinate);
    infoWindow.open({ map });
    setInfoContainer(container);

    return () => {
      infoWindow.close();
    };
  }, [selectedListingId, fuzzyConfig]);

  const selectedListing = selectedListingId
    ? listings.find(l => l.id.uuid === selectedListingId)
    : null;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <div ref={mapContainerRef} className={css.mapContainer} />
      {infoContainer && selectedListing
        ? ReactDOM.createPortal(
            <SearchMapInfoCard
              listings={[selectedListing]}
              createURLToListing={createURLToListing}
              onListingInfoCardClicked={onListingInfoCardClicked}
              onClose={() => setSelectedListingId(null)}
              config={config}
            />,
            infoContainer
          )
        : null}
    </div>
  );
};

export default SearchMapClusteredGoogleMaps;
